import React, { useState, useEffect } from 'react';
import { adminService } from '../../../services/adminService';
import Icon from '../../../components/AppIcon';

const JwtFailuresPanel = ({ compact = false, limit = 50 }) => {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    errorType: ''
  });

  useEffect(() => {
    loadFailures();
  }, [filters]);

  const loadFailures = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService?.getJwtValidationFailures({
        limit,
        errorType: filters?.errorType || null
      });
      setFailures(data);
    } catch (err) {
      console.error('Load JWT failures error:', err);
      setError(err?.message || 'Failed to load JWT validation failures');
    } finally {
      setLoading(false);
    }
  };

  const getErrorTypeConfig = (errorType) => {
    const configs = {
      token_expired: { label: 'Token Expired', color: 'text-warning', bgColor: 'bg-warning/10', icon: 'Clock' },
      invalid_signature: { label: 'Invalid Signature', color: 'text-destructive', bgColor: 'bg-destructive/10', icon: 'ShieldAlert' },
      malformed_token: { label: 'Malformed Token', color: 'text-destructive', bgColor: 'bg-destructive/10', icon: 'AlertTriangle' },
      missing_token: { label: 'Missing Token', color: 'text-muted-foreground', bgColor: 'bg-muted', icon: 'XCircle' }
    };
    return configs?.[errorType] || { label: errorType, color: 'text-muted-foreground', bgColor: 'bg-muted', icon: 'AlertCircle' };
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-3">
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
            <div className="h-16 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="ShieldAlert" size={20} className="text-warning" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">JWT Validation Failures</h2>
            <p className="text-sm text-muted-foreground">Authentication issue tracking</p>
          </div>
        </div>
        {!compact && (
          <button
            onClick={loadFailures}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="RefreshCw" size={16} className="text-muted-foreground" />
          </button>
        )}
      </div>

      {!compact && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-foreground mb-2">Error Type</label>
          <select
            value={filters?.errorType}
            onChange={(e) => setFilters({ ...filters, errorType: e?.target?.value })}
            className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Error Types</option>
            <option value="token_expired">Token Expired</option>
            <option value="invalid_signature">Invalid Signature</option>
            <option value="malformed_token">Malformed Token</option>
            <option value="missing_token">Missing Token</option>
          </select>
        </div>
      )}

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-4 flex items-start gap-3">
          <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-destructive text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {failures?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No JWT validation failures found</p>
          </div>
        ) : (
          failures?.map((failure) => {
            const errorConfig = getErrorTypeConfig(failure?.errorType);
            return (
              <div key={failure?.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${errorConfig?.bgColor}`}>
                        <Icon name={errorConfig?.icon} size={12} className={errorConfig?.color} />
                        <span className={`text-xs font-medium ${errorConfig?.color}`}>{errorConfig?.label}</span>
                      </div>
                    </div>
                    <p className="text-sm text-foreground mb-2">{failure?.errorMessage}</p>
                    {failure?.requestPath && (
                      <p className="text-xs text-muted-foreground mb-1">Path: {failure?.requestPath}</p>
                    )}
                    {failure?.ipAddress && (
                      <p className="text-xs text-muted-foreground mb-1">IP: {failure?.ipAddress}</p>
                    )}
                    {failure?.tokenPreview && (
                      <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mb-2 font-mono truncate">
                        {failure?.tokenPreview}
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>{adminService?.formatRelativeTime(failure?.createdAt)}</span>
                      <span>{adminService?.formatDate(failure?.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default JwtFailuresPanel;